import { useLocation, useNavigate } from 'react-router-dom';

import { InfoModal } from '@backrs_ui/common-ui';

import { ReactComponent as BackrsFilledDropShadow } from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ContactPromptModal } from '@backrs_ui/grow-ui/src/components/ContactPromptModal/ContactPromptModal';
import { OnboardingFormError } from '@backrs_ui/grow-ui/src/pages/Onboarding/OnboardingPageContainer';
import { SignUpForm, SignUpFormData } from '@backrs_ui/grow-ui/src/pages/Onboarding/SignUpForm';
import { parseName } from '@backrs_ui/grow-ui/src/pages/Onboarding/utils/onboarding.utils';
import {
  SignupPromptContext,
  closeSignupPrompt,
  selectSignupPromptContext,
  selectSignupPromptOpen,
} from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { usePostUserMutation } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import { selectBoosterDraft } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import type { RootState } from '@backrs_ui/grow-ui/src/redux/store';
import { selectUserResponseSet } from '@backrs_ui/grow-ui/src/redux/userResponse/userResponse';
import { handleAfterLogin } from '@backrs_ui/grow-ui/src/utils/loginHelper';
import { getGuestTokenIfPresent } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { Box, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.signupPromptModal;

type PromptContextConfig = Readonly<{
  subtitle?: string;
  /** 'contact' offers a way to get set up for real instead of an account form. */
  variant?: 'signup' | 'contact';
  destination?: (typeof APP_ROUTE)[keyof typeof APP_ROUTE]; // omitted = stay on the current page
  /** The context carries its own intake, so signup finishes here instead of in onboarding. */
  skipOnboarding?: boolean;
  /** The context is only reachable by a student, so the role is known at registration. */
  forceStudent?: boolean;
  /** Zip fallback when the Blueprint has none, read from the context's own draft. */
  zipSelector?: (state: RootState) => string | null | undefined;
}>;

const PROMPT_CONTEXT_CONFIG: Record<SignupPromptContext, PromptContextConfig> = {
  general: { subtitle: STRINGS.subtitle },
  blueprint: { subtitle: STRINGS.subtitleBlueprint, destination: APP_ROUTE.MY_BLUEPRINT },
  sat_act_booster: {
    subtitle: STRINGS.subtitleSatActBooster,
    destination: APP_ROUTE.SAT_ACT_BOOSTER_RESULT,
    skipOnboarding: true,
    forceStudent: true,
    zipSelector: (state) => selectBoosterDraft(state).zip,
  },
  sandbox: { variant: 'contact' },
};

export const SignupPromptModal = () => {
  const open = useAppSelector(selectSignupPromptOpen);
  const promptContext = useAppSelector(selectSignupPromptContext);
  const userResponseSet = useAppSelector(selectUserResponseSet);
  const mostRecentSchoolLevel = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.school_level,
  );
  const mostRecentZipCode = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.zip_code,
  );
  const guestHasBlueprint = useAppSelector(
    (state) => !!state.userResponseSet.mostRecentUserResponseSet?.user_responses?.length,
  );
  const config = PROMPT_CONTEXT_CONFIG[promptContext];
  const contextZip = useAppSelector((state) => config.zipSelector?.(state));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [postUser] = usePostUserMutation();

  const handleClose = () => dispatch(closeSignupPrompt());

  // Pass the guest token as Bearer so the server claims the persisted work, then swap it for the real one.
  const handleSignUpComplete = async (data: SignUpFormData) => {
    // Without a Blueprint we lack zip + enrollment, so finish in onboarding — unless the context
    // carries its own intake, in which case signup lands at its destination instead.
    if (!guestHasBlueprint && !config.skipOnboarding) {
      handleClose();
      navigate(APP_ROUTE.SIGN_UP, { state: { signUpData: data } });

      return;
    }

    const { first_name, last_name } = parseName(data.name);
    const guestToken = getGuestTokenIfPresent();

    // only students get a role at registration.
    const schoolLevel = userResponseSet.school_level ?? mostRecentSchoolLevel;
    const isStudent =
      config.forceStudent || (!!schoolLevel && schoolLevel !== 'no_current_enrollment');
    const zipCode = userResponseSet.zip_code ?? mostRecentZipCode ?? contextZip;

    const { user } = await postUser({
      userData: {
        first_name,
        last_name,
        phone: data.phone,
        email: data.email,
        password: data.password,
        ...(isStudent ? { onboarding_type: 'student' } : {}),
        ...(zipCode ? { zip_code: zipCode } : {}),
      },
      guestToken: guestToken ?? undefined,
    }).unwrap();

    // The account now exists, so retrying the form would fail on duplicate email —
    // surface login failures as a "log in instead" error rather than the generic one.
    try {
      if (!user?.access_token) {
        throw new Error('Account created but no access token was returned');
      }

      await handleAfterLogin({
        access_token: user.access_token,
        dispatch,
        navigate,
        destination: config.destination ?? `${location.pathname}${location.search}`,
        setErrorMessage: (message) => {
          throw new Error(message);
        },
      });
    } catch (error) {
      console.error('Sign-up succeeded but login failed', error);
      throw new OnboardingFormError({ root: STRINGS.accountCreatedLoginFailed });
    }

    handleClose();
  };

  const handleLoginClick = () => {
    handleClose();
    navigate(APP_ROUTE.LOGIN);
  };

  if (config.variant === 'contact') {
    return <ContactPromptModal open={open} onClose={handleClose} />;
  }

  return (
    <InfoModal
      open={open}
      onClose={handleClose}
      width="xs"
      hideCircleImage
      headerVisual={<BackrsFilledDropShadow width="32px" height="32px" />}
      headerContent={
        <Box sx={headerContentSx}>
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {STRINGS.title}
          </Typography>
          <Typography variant="text-sm-regular" sx={subtitleSx}>
            {config.subtitle}
          </Typography>
        </Box>
      }
      bodyContent={
        <Box sx={bodySx}>
          <SignUpForm
            onSignUpComplete={handleSignUpComplete}
            onLoginClick={handleLoginClick}
            buttonLabel="Sign Up"
            buttonId="signup-prompt-modal-submit-btn"
            loginLinkId="signup-prompt-modal-login-link"
          />
        </Box>
      }
    />
  );
};

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  px: backrs.spacing['spacing-xl'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  mt: `-${backrs.spacing['spacing-xl']}`,
  px: backrs.spacing['spacing-xl'],
  pb: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
